import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Handshake, TrendingUp, CheckCircle2, Clock, XCircle, Loader2,
  Users, BarChart3, Zap, Sun, Tv, BatteryCharging, Shield,
  Filter, ArrowLeft, ArrowUpDown, User, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  concluido: 'bg-emerald-100 text-emerald-700',
  em_processo: 'bg-blue-100 text-blue-700',
  pendente: 'bg-amber-100 text-amber-700',
  anulado: 'bg-red-100 text-red-700',
  pedido_chamada: 'bg-slate-100 text-slate-700',
  agendado: 'bg-cyan-100 text-cyan-700',
};
const STATUS_LABELS = {
  concluido: 'Concluido', em_processo: 'Em Processo', pendente: 'Pendente',
  anulado: 'Anulado', pedido_chamada: 'Ped. Chamada', agendado: 'Agendado',
};
const CATEGORY_ICONS = {
  energias: Zap, luz_gas: Zap, solar: Sun,
  telecomunicacoes: Tv, carregamento: BatteryCharging, alarmes: Shield,
};
const CATEGORY_COLORS = {
  energias: 'text-yellow-500', luz_gas: 'text-yellow-500', solar: 'text-amber-500',
  telecomunicacoes: 'text-purple-500', carregamento: 'text-green-500', alarmes: 'text-red-500',
};

export const PartnerDetailPage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API_URL}/dashboard/partners/${partnerId}`, { params });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [partnerId]);

  const handleFilter = () => fetchData();
  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(fetchData, 0);
  };

  const handleExportExcel = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API_URL}/dashboard/partners/${partnerId}/excel`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename="')[1]?.replace('"', '') : 'producao_parceiro.xlsx';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao exportar Excel');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const comerciais = data?.comerciais || [];
  const byProduct = data?.by_product || {};
  const byStatus = data?.by_status || {};
  const total = data?.total_contracts || 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="partner-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/parceiros')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
                <Handshake className="w-6 h-6 text-[#2D3E6F]" />
                {data?.partner_name || 'Parceiro'}
              </h1>
              <p className="text-slate-500 mt-0.5">{data?.team_count || 0} membros na equipa</p>
            </div>
          </div>
          <Button
            onClick={handleExportExcel}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2 rounded-full"
            data-testid="export-partner-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" /> Exportar Excel
          </Button>
        </div>

        {/* Date filter */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-end gap-4">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Data Inicio</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-10 rounded-lg" data-testid="filter-start-date" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Data Fim</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-10 rounded-lg" data-testid="filter-end-date" />
              </div>
              <Button onClick={handleFilter} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2 h-10" data-testid="filter-apply-btn">
                <Filter className="w-4 h-4" /> Filtrar
              </Button>
              {(startDate || endDate) && (
                <Button onClick={handleClearFilter} variant="outline" className="h-10">Limpar</Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{total}</p>
                  <p className="text-xs text-slate-500">Total Contratos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{byStatus?.concluido || 0}</p>
                  <p className="text-xs text-slate-500">Concluidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{byStatus?.pendente || 0}</p>
                  <p className="text-xs text-slate-500">Pendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data?.team_count || 0}</p>
                  <p className="text-xs text-slate-500">Equipa</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Product breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Tipo de Contrato</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.values(byProduct).map((p) => {
                  const pct = total > 0 ? (p.count / total * 100) : 0;
                  const Icon = CATEGORY_ICONS[p.category] || Zap;
                  return (
                    <div key={`${p.category}:${p.product}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-1.5">
                          <Icon className={`w-4 h-4 ${CATEGORY_COLORS[p.category] || ''}`} />
                          {p.label}
                        </span>
                        <span className="font-medium">{p.count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-[#4DD4E8] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(byProduct).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Estado dos Contratos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(byStatus).map(([status, count]) => {
                  const pct = total > 0 ? (count / total * 100) : 0;
                  return (
                    <div key={status}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'}`}>
                          {STATUS_LABELS[status] || status}
                        </span>
                        <span className="font-medium">{count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-[#2D3E6F] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(byStatus).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team ranking */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5 text-[#2D3E6F]" />
              Equipa — Producao Individual
            </CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold w-12">#</TableHead>
                  <TableHead className="font-semibold">Comercial</TableHead>
                  <TableHead className="font-semibold">Funcao</TableHead>
                  <TableHead className="font-semibold text-center">Total</TableHead>
                  <TableHead className="font-semibold text-center">Concluidos</TableHead>
                  <TableHead className="font-semibold text-center">Pendentes</TableHead>
                  <TableHead className="font-semibold text-center">Anulados</TableHead>
                  <TableHead className="font-semibold">Tipos de Contrato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comerciais.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400">Sem membros na equipa</TableCell>
                  </TableRow>
                ) : (
                  comerciais.map((c, i) => (
                    <TableRow
                      key={c.user_id}
                      className="hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => navigate(`/dashboard/comerciais/${c.user_id}`)}
                      data-testid={`comercial-row-${i}`}
                    >
                      <TableCell className="font-bold text-[#2D3E6F]">{i + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#2D3E6F] flex items-center justify-center text-white text-xs font-bold">
                            {c.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium">{c.user_name}</span>
                            <p className="text-xs text-slate-400">{c.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{c.role}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-lg font-bold text-slate-900">{c.total_contracts}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">{c.by_status?.concluido || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{c.by_status?.pendente || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{c.by_status?.anulado || 0}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Object.values(c.by_product || {}).map((p) => {
                            const Icon = CATEGORY_ICONS[p.category] || Zap;
                            return (
                              <span key={`${p.category}:${p.product}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                                <Icon className={`w-3 h-3 ${CATEGORY_COLORS[p.category] || ''}`} />
                                {p.label}: {p.count}
                              </span>
                            );
                          })}
                          {Object.keys(c.by_product || {}).length === 0 && <span className="text-xs text-slate-400">-</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </Layout>
  );
};
